import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Activity,
  RefreshCw,
  RotateCcw,
  Play,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Layers,
  Users,
  MapPin,
} from 'lucide-react';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorState } from '@/components/common/ErrorState';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { useToast } from '@/context/ToastContext';
import { useI18n } from '@/context/I18nContext';
import { rpc } from '@/api/client';

interface QueueCounters {
  pendingCount?: number;
  processingCount?: number;
  completedCount?: number;
  failedCount?: number;
  totalEligibleCount?: number;
}

interface AdminUserQueueStats {
  userId: string;
  userName?: string;
  userEmail?: string;
  pendingCount?: number;
  processingCount?: number;
  completedCount?: number;
  failedCount?: number;
  totalEnqueued?: number;
  updatedAt?: string;
}

interface AdminTrackQueueStats {
  trackId: string;
  trackName: string;
  trackDataHash?: string;
  totalLaps?: number;
  upToDateLaps?: number;
  outdatedLaps?: number;
  pendingLaps?: number;
  failedLaps?: number;
  totalSessions?: number;
  analyzedSessions?: number;
}

interface GetAdminQueueMonitoringStatsResponse {
  isActive?: boolean;
  lapQueue?: QueueCounters;
  sessionQueue?: QueueCounters;
  userQueues?: AdminUserQueueStats[];
  trackQueues?: AdminTrackQueueStats[];
}

interface TriggerAdminQueueActionResponse {
  enqueuedCount?: number;
  skippedCount?: number;
}

export function MonitoringTab() {
  const { t } = useI18n();
  const { showToast } = useToast();

  const [stats, setStats] = useState<GetAdminQueueMonitoringStatsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fetchingRef = useRef(false);

  const [syncingUnanalyzed, setSyncingUnanalyzed] = useState(false);
  const [retryingFailed, setRetryingFailed] = useState(false);
  const [showConfirmReanalyzeAll, setShowConfirmReanalyzeAll] = useState(false);
  const [reanalyzingAll, setReanalyzingAll] = useState(false);

  const [reanalyzingTrackId, setReanalyzingTrackId] = useState<string | null>(null);
  const [confirmReanalyzeTrack, setConfirmReanalyzeTrack] = useState<{
    trackId: string;
    trackName: string;
    skipped: number;
  } | null>(null);

  const fetchStats = useCallback(async (silent = false) => {
    if (silent && fetchingRef.current) {
      return;
    }
    fetchingRef.current = true;
    if (!silent) {
      setRefreshing(true);
    }
    try {
      const data = await rpc<GetAdminQueueMonitoringStatsResponse>('GetAdminQueueMonitoringStats', {});
      setStats(data);
      setError(null);
    } catch (e) {
      if (!silent) {
        setError((e as Error).message);
      }
    } finally {
      fetchingRef.current = false;
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const isQueueActive = Boolean(
    stats?.isActive ||
      (stats?.lapQueue?.pendingCount ?? 0) > 0 ||
      (stats?.lapQueue?.processingCount ?? 0) > 0 ||
      (stats?.sessionQueue?.pendingCount ?? 0) > 0 ||
      (stats?.sessionQueue?.processingCount ?? 0) > 0,
  );

  useEffect(() => {
    const intervalMs = isQueueActive ? 1500 : 8000;
    const timer = setInterval(() => {
      fetchStats(true);
    }, intervalMs);
    return () => clearInterval(timer);
  }, [fetchStats, isQueueActive]);

  const handleSyncUnanalyzed = async () => {
    setSyncingUnanalyzed(true);
    try {
      const res = await rpc<TriggerAdminQueueActionResponse>('TriggerAdminQueueAction', {
        action: 'sync_unanalyzed',
      });
      const enqueued = res.enqueuedCount ?? 0;
      const skipped = res.skippedCount ?? 0;
      showToast(
        t('monitoring.syncUnanalyzedDone', {
          enqueued: String(enqueued),
          skipped: String(skipped),
        }),
      );
      await fetchStats(true);
    } catch (e) {
      showToast((e as Error).message, 'error');
    } finally {
      setSyncingUnanalyzed(false);
    }
  };

  const handleRetryFailed = async () => {
    setRetryingFailed(true);
    try {
      const res = await rpc<TriggerAdminQueueActionResponse>('TriggerAdminQueueAction', {
        action: 'retry_failed',
      });
      const retried = res.enqueuedCount ?? 0;
      showToast(t('monitoring.retryFailedDone', { retried: String(retried) }));
      await fetchStats(true);
    } catch (e) {
      showToast((e as Error).message, 'error');
    } finally {
      setRetryingFailed(false);
    }
  };

  const handleConfirmReanalyzeAll = async () => {
    setReanalyzingAll(true);
    try {
      const res = await rpc<TriggerAdminQueueActionResponse>('TriggerAdminQueueAction', {
        action: 'reanalyze_all',
      });
      const enqueued = res.enqueuedCount ?? 0;
      setShowConfirmReanalyzeAll(false);
      showToast(t('tracks.reanalyzeForceQueued', { enqueued: String(enqueued) }));
      await fetchStats(true);
    } catch (e) {
      showToast((e as Error).message, 'error');
    } finally {
      setReanalyzingAll(false);
    }
  };

  const handleReanalyzeTrack = async (track: AdminTrackQueueStats) => {
    setReanalyzingTrackId(track.trackId);
    try {
      const res = await rpc<{ enqueuedLaps?: number; skippedUpToDateLaps?: number }>(
        'RequeueTrackLapAnalysis',
        {
          trackId: track.trackId,
          source: 'canonical',
          force: false,
        },
      );
      const enqueued = res.enqueuedLaps ?? 0;
      const skipped = res.skippedUpToDateLaps ?? 0;
      if (enqueued > 0) {
        showToast(
          t('tracks.reanalyzeQueued', {
            enqueued: String(enqueued),
            skipped: String(skipped),
          }),
        );
        await fetchStats(true);
      } else {
        setConfirmReanalyzeTrack({
          trackId: track.trackId,
          trackName: track.trackName,
          skipped,
        });
      }
    } catch (e) {
      showToast((e as Error).message, 'error');
    } finally {
      setReanalyzingTrackId(null);
    }
  };

  const handleConfirmTrackReanalyze = async () => {
    if (!confirmReanalyzeTrack) return;
    const { trackId } = confirmReanalyzeTrack;
    setReanalyzingTrackId(trackId);
    try {
      const res = await rpc<{ enqueuedLaps?: number }>('RequeueTrackLapAnalysis', {
        trackId,
        source: 'canonical',
        force: true,
      });
      const enqueued = res.enqueuedLaps ?? 0;
      setConfirmReanalyzeTrack(null);
      showToast(t('tracks.reanalyzeForceQueued', { enqueued: String(enqueued) }));
      await fetchStats(true);
    } catch (e) {
      showToast((e as Error).message, 'error');
    } finally {
      setReanalyzingTrackId(null);
    }
  };

  if (loading && !stats) {
    return <LoadingSpinner />;
  }

  if (error && !stats) {
    return <ErrorState message={error} />;
  }

  const lapQ = stats?.lapQueue ?? {};
  const sessQ = stats?.sessionQueue ?? {};
  const userQueues = stats?.userQueues ?? [];
  const trackQueues = stats?.trackQueues ?? [];
  const totalFailed = (lapQ.failedCount ?? 0) + (sessQ.failedCount ?? 0);

  const renderQueueCard = (title: string, q: QueueCounters) => {
    const pending = q.pendingCount ?? 0;
    const processing = q.processingCount ?? 0;
    const completed = q.completedCount ?? 0;
    const failed = q.failedCount ?? 0;
    const totalEligible = q.totalEligibleCount ?? 0;
    const denom = totalEligible > 0 ? totalEligible : pending + processing + completed + failed;
    const pct = denom > 0 ? Math.round((completed / denom) * 100) : 100;

    return (
      <div className="card p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Layers size={15} className="text-accent" />
            <h3 className="text-sm font-semibold text-text-primary">{title}</h3>
          </div>
          <span className="text-xs text-text-muted tabular-nums">
            {t('monitoring.totalEligible')}: <strong className="text-text-secondary">{totalEligible}</strong>
          </span>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
          <div className="p-2.5 rounded-md bg-bg-surface border border-border-subtle">
            <div className="flex items-center gap-1.5 text-[0.7rem] text-text-muted uppercase tracking-wider">
              <Clock size={12} className="text-amber-400" />
              {t('monitoring.pending')}
            </div>
            <div className="text-lg font-semibold text-text-primary tabular-nums mt-0.5">{pending}</div>
          </div>

          <div className="p-2.5 rounded-md bg-bg-surface border border-border-subtle">
            <div className="flex items-center gap-1.5 text-[0.7rem] text-text-muted uppercase tracking-wider">
              <Activity size={12} className={processing > 0 ? 'text-accent animate-pulse' : 'text-accent'} />
              {t('monitoring.processing')}
            </div>
            <div className="text-lg font-semibold text-text-primary tabular-nums mt-0.5">{processing}</div>
          </div>

          <div className="p-2.5 rounded-md bg-bg-surface border border-border-subtle">
            <div className="flex items-center gap-1.5 text-[0.7rem] text-text-muted uppercase tracking-wider">
              <CheckCircle2 size={12} className="text-emerald-400" />
              {t('monitoring.completed')}
            </div>
            <div className="text-lg font-semibold text-text-primary tabular-nums mt-0.5">{completed}</div>
          </div>

          <div className="p-2.5 rounded-md bg-bg-surface border border-border-subtle">
            <div className="flex items-center gap-1.5 text-[0.7rem] text-text-muted uppercase tracking-wider">
              <AlertTriangle size={12} className={failed > 0 ? 'text-red' : 'text-text-muted'} />
              {t('monitoring.failed')}
            </div>
            <div className={`text-lg font-semibold tabular-nums mt-0.5 ${failed > 0 ? 'text-red' : 'text-text-primary'}`}>
              {failed}
            </div>
          </div>
        </div>

        {denom > 0 && (
          <div className="space-y-1">
            <div className="flex justify-between text-[0.7rem] text-text-muted tabular-nums">
              <span>{t('monitoring.progress')}</span>
              <span>
                {completed} / {denom} ({pct}%)
              </span>
            </div>
            <div className="w-full h-1.5 bg-bg-surface rounded-full overflow-hidden">
              <div
                className="h-full bg-accent transition-all duration-300"
                style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
              />
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-semibold tracking-tight">{t('monitoring.title')}</h2>
          <p className="text-xs text-text-muted mt-0.5">{t('monitoring.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2.5">
          {isQueueActive && (
            <span className="inline-flex items-center gap-1.5 text-xs text-accent">
              <Activity size={13} className="animate-pulse" />
              {t('monitoring.autoRefreshActive')}
            </span>
          )}
          <button
            onClick={() => fetchStats(false)}
            disabled={refreshing}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium
              bg-bg-raised text-text-secondary border border-border-default hover:border-border-hover hover:text-text-primary
              transition-colors cursor-pointer disabled:opacity-50"
          >
            <RefreshCw size={13} className={refreshing ? 'animate-spin text-accent' : ''} />
            {t('monitoring.refresh')}
          </button>
        </div>
      </div>

      {/* Global Queue Counters */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {renderQueueCard(t('monitoring.lapQueueTitle'), lapQ)}
        {renderQueueCard(t('monitoring.sessionQueueTitle'), sessQ)}
      </div>

      {/* Global Queue Action Controls */}
      <div className="card p-4 space-y-3">
        <h3 className="text-sm font-semibold text-text-primary">{t('monitoring.actionsTitle')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {/* Sync Unanalyzed & Outdated */}
          <div className="p-3 rounded-md bg-bg-surface border border-border-subtle flex flex-col justify-between gap-3">
            <div>
              <div className="text-xs font-medium text-text-primary">{t('monitoring.syncUnanalyzed')}</div>
              <p className="text-[0.7rem] text-text-muted mt-1">{t('monitoring.syncUnanalyzedDesc')}</p>
            </div>
            <button
              onClick={handleSyncUnanalyzed}
              disabled={syncingUnanalyzed}
              className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium
                bg-bg-raised text-text-primary border border-border-default hover:border-border-hover
                transition-colors cursor-pointer disabled:opacity-50 self-start"
            >
              <Play size={13} className={syncingUnanalyzed ? 'animate-pulse text-accent' : 'text-accent'} />
              {t('monitoring.syncUnanalyzed')}
            </button>
          </div>

          {/* Retry Failed Jobs */}
          <div className="p-3 rounded-md bg-bg-surface border border-border-subtle flex flex-col justify-between gap-3">
            <div>
              <div className="text-xs font-medium text-text-primary">{t('monitoring.retryFailed')}</div>
              <p className="text-[0.7rem] text-text-muted mt-1">{t('monitoring.retryFailedDesc')}</p>
            </div>
            <button
              onClick={handleRetryFailed}
              disabled={retryingFailed || totalFailed === 0}
              className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium
                bg-bg-raised text-text-primary border border-border-default hover:border-border-hover
                transition-colors cursor-pointer disabled:opacity-40 self-start"
            >
              <RotateCcw size={13} className={retryingFailed ? 'animate-spin text-amber-400' : 'text-amber-400'} />
              {t('monitoring.retryFailed')} ({totalFailed})
            </button>
          </div>

          {/* Force Re-analyze All Laps */}
          <div className="p-3 rounded-md bg-bg-surface border border-border-subtle flex flex-col justify-between gap-3">
            <div>
              <div className="text-xs font-medium text-text-primary">{t('monitoring.reanalyzeAll')}</div>
              <p className="text-[0.7rem] text-text-muted mt-1">{t('monitoring.reanalyzeAllDesc')}</p>
            </div>
            <button
              onClick={() => setShowConfirmReanalyzeAll(true)}
              disabled={reanalyzingAll}
              className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium
                bg-accent text-white hover:bg-accent-hover transition-colors cursor-pointer disabled:opacity-50 self-start"
            >
              <Activity size={13} className={reanalyzingAll ? 'animate-pulse' : ''} />
              {t('monitoring.reanalyzeAll')}
            </button>
          </div>
        </div>
      </div>

      {/* Per-User Queue Progress (Round-Robin) */}
      <div className="card overflow-hidden">
        <div className="px-4 py-3 border-b border-border-subtle flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="flex items-center gap-2">
              <Users size={15} className="text-accent" />
              <h3 className="text-sm font-semibold text-text-primary">{t('monitoring.userQueuesTitle')}</h3>
            </div>
            <p className="text-[0.7rem] text-text-muted mt-0.5">{t('monitoring.userQueuesSubtitle')}</p>
          </div>
        </div>

        {userQueues.length === 0 ? (
          <div className="p-6 text-center text-xs text-text-muted">{t('monitoring.userQueuesEmpty')}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="divider">
                  <th className="px-4 py-2.5 text-left text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">
                    {t('monitoring.user')}
                  </th>
                  <th className="px-4 py-2.5 text-left text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">
                    {t('monitoring.pending')}
                  </th>
                  <th className="px-4 py-2.5 text-left text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">
                    {t('monitoring.processing')}
                  </th>
                  <th className="px-4 py-2.5 text-left text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">
                    {t('monitoring.completed')}
                  </th>
                  <th className="px-4 py-2.5 text-left text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">
                    {t('monitoring.failed')}
                  </th>
                  <th className="w-[200px] px-4 py-2.5 text-left text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">
                    {t('monitoring.progress')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {userQueues.map((uq, i) => {
                  const pending = uq.pendingCount ?? 0;
                  const processing = uq.processingCount ?? 0;
                  const done = uq.completedCount ?? 0;
                  const failed = uq.failedCount ?? 0;
                  const total = uq.totalEnqueued ?? pending + processing + done + failed;
                  const pct = total > 0 ? Math.round((done / total) * 100) : 100;
                  const isSystem = uq.userId === 'system';

                  return (
                    <tr
                      key={uq.userId}
                      className={`hover:bg-bg-hover transition-colors ${i < userQueues.length - 1 ? 'divider' : ''}`}
                    >
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-text-primary">
                          {isSystem ? t('monitoring.systemQueue') : uq.userName || uq.userId}
                        </div>
                        {!isSystem && uq.userEmail && (
                          <div className="text-[0.7rem] text-text-muted">{uq.userEmail}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-amber-400 font-medium tabular-nums">
                        {pending}
                      </td>
                      <td className="px-4 py-3 text-xs text-accent font-medium tabular-nums">
                        {processing}
                      </td>
                      <td className="px-4 py-3 text-xs text-emerald-400 font-medium tabular-nums">
                        {done}
                      </td>
                      <td className="px-4 py-3 text-xs tabular-nums">
                        <span className={failed > 0 ? 'text-red font-medium' : 'text-text-muted'}>
                          {failed}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="space-y-1">
                          <div className="flex justify-between text-[0.7rem] text-text-muted tabular-nums">
                            <span>
                              {done} / {total}
                            </span>
                            <span>{pct}%</span>
                          </div>
                          <div className="w-full h-1.5 bg-bg-surface rounded-full overflow-hidden">
                            <div
                              className="h-full bg-accent transition-all duration-300"
                              style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
                            />
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Track Analysis Coverage & Re-trigger Controls */}
      <div className="card overflow-hidden">
        <div className="px-4 py-3 border-b border-border-subtle flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="flex items-center gap-2">
              <MapPin size={15} className="text-accent" />
              <h3 className="text-sm font-semibold text-text-primary">{t('monitoring.tracksTitle')}</h3>
            </div>
            <p className="text-[0.7rem] text-text-muted mt-0.5">{t('monitoring.tracksSubtitle')}</p>
          </div>
        </div>

        {trackQueues.length === 0 ? (
          <div className="p-6 text-center text-xs text-text-muted">{t('monitoring.tracksEmpty')}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="divider">
                  <th className="px-4 py-2.5 text-left text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">
                    {t('tracks.name')}
                  </th>
                  <th className="px-4 py-2.5 text-left text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">
                    {t('monitoring.upToDateLaps')}
                  </th>
                  <th className="px-4 py-2.5 text-left text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">
                    {t('monitoring.outdatedLaps')}
                  </th>
                  <th className="px-4 py-2.5 text-left text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">
                    {t('monitoring.pending')}
                  </th>
                  <th className="w-[160px] px-4 py-2.5 text-left text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">
                    {t('monitoring.trackCoverage')}
                  </th>
                  <th className="w-[150px] px-4 py-2.5 text-right text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted" />
                </tr>
              </thead>
              <tbody>
                {trackQueues.map((tq, i) => {
                  const total = tq.totalLaps ?? 0;
                  const upToDate = tq.upToDateLaps ?? 0;
                  const outdated = tq.outdatedLaps ?? 0;
                  const pending = tq.pendingLaps ?? 0;
                  const pct = total > 0 ? Math.round((upToDate / total) * 100) : 100;
                  const isBusy = reanalyzingTrackId === tq.trackId;
                  const hasShape = Boolean(tq.trackDataHash && tq.trackDataHash !== 'unlinked');

                  return (
                    <tr
                      key={tq.trackId}
                      className={`hover:bg-bg-hover transition-colors ${i < trackQueues.length - 1 ? 'divider' : ''}`}
                    >
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-text-primary">{tq.trackName}</div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span
                            className={`text-[0.65rem] px-1.5 py-0.5 rounded ${
                              hasShape
                                ? 'bg-emerald-500/10 text-emerald-400'
                                : 'bg-amber-500/10 text-amber-400'
                            }`}
                          >
                            {hasShape ? t('monitoring.shapeReady') : t('monitoring.noShape')}
                          </span>
                          {hasShape && tq.trackDataHash && (
                            <span className="text-[0.65rem] text-text-muted font-mono">
                              #{tq.trackDataHash.slice(0, 8)}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-emerald-400 font-medium tabular-nums">
                        {upToDate} / {total}
                      </td>
                      <td className="px-4 py-3 text-xs tabular-nums">
                        <span className={outdated > 0 ? 'text-amber-400 font-medium' : 'text-text-muted'}>
                          {outdated}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs tabular-nums">
                        <span className={pending > 0 ? 'text-accent font-medium' : 'text-text-muted'}>
                          {pending}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="space-y-1">
                          <div className="flex justify-between text-[0.7rem] text-text-muted tabular-nums">
                            <span>{pct}%</span>
                          </div>
                          <div className="w-full h-1.5 bg-bg-surface rounded-full overflow-hidden">
                            <div
                              className="h-full bg-emerald-400 transition-all duration-300"
                              style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
                            />
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => handleReanalyzeTrack(tq)}
                          disabled={isBusy}
                          title={t('tracks.reanalyzeLaps')}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium
                            bg-bg-raised text-text-secondary border border-border-default hover:border-border-hover hover:text-text-primary
                            transition-colors cursor-pointer disabled:opacity-40"
                        >
                          <Activity size={13} className={isBusy ? 'animate-pulse text-accent' : 'text-accent'} />
                          {t('monitoring.reanalyzeTrack')}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Confirmation Dialog: Per-Track Force Recalculation */}
      <ConfirmDialog
        open={confirmReanalyzeTrack !== null}
        variant="primary"
        title={t('tracks.reanalyzeConfirmTitle')}
        message={
          confirmReanalyzeTrack
            ? t('tracks.reanalyzeConfirmMessage', {
                name: confirmReanalyzeTrack.trackName,
                count: String(confirmReanalyzeTrack.skipped),
              })
            : ''
        }
        confirmLabel={t('tracks.reanalyzeConfirmButton')}
        loading={confirmReanalyzeTrack !== null && reanalyzingTrackId === confirmReanalyzeTrack.trackId}
        onConfirm={handleConfirmTrackReanalyze}
        onCancel={() => setConfirmReanalyzeTrack(null)}
      />

      {/* Confirmation Dialog: Full Recalculation Across All Tracks */}
      <ConfirmDialog
        open={showConfirmReanalyzeAll}
        variant="primary"
        title={t('tracks.reanalyzeAllConfirmTitle')}
        message={t('tracks.reanalyzeAllConfirmMessage')}
        confirmLabel={t('tracks.reanalyzeConfirmButton')}
        loading={reanalyzingAll}
        onConfirm={handleConfirmReanalyzeAll}
        onCancel={() => setShowConfirmReanalyzeAll(false)}
      />
    </div>
  );
}
